export const TRANSLATION_PREFIX = 'STATIC.PAGES.REFERENCE_TABLE';

// Initial number of rows rendered, and how many more are revealed per infinite-scroll tick.
export const INITIAL_ROW_LIMIT = 250;
export const ROW_LIMIT_INCREMENT = 10;

// Rows are edited in the browser; larger tables must be imported/exported instead of hand-saved.
export const MAX_ROWS_FOR_MANUAL_CHANGES = 10000;

export const MAX_PHYSICAL_ROWS_TO_SAVE = 30000;

export const SUCCESS_TOAST_AUTO_CLOSE = 5000;

export const VALUE_CURRENCY_FIELD = 'value_currency';
export const EVENT_FIELD = 'event';
export const COUNTRY_FIELD = 'country';
export const ADN_ACCOUNT_ID_FIELD = 'adn_account_id';
export const COLLAPSED_FIELDS = [COUNTRY_FIELD];
export const COHORT_PERIOD_FIELD = 'cohort_period';

// The cohort-period option `name` (a stringified period id) that stands for "Actual". A stored NULL
// cohort_period is served to the grid as this id, and a brand-new row's cell is null until picked;
// both mean Actual, so the conflict check collapses them to this value. Mirrors the backend
// ACTUAL_COHORT_ID (-2).
export const ACTUAL_COHORT_PERIOD_NAME = '-2';
// A new row starts on Actual rather than blank. Blank saved as Actual anyway, which meant the window a row
// was priced on was decided by a default the user never saw. The server always labels this option "Actual"
// (ACTUAL_COHORT_NAME), so the cell matches the offered option without waiting for the dictionary.
export const ACTUAL_COHORT_PERIOD_CELL = { name: ACTUAL_COHORT_PERIOD_NAME, display_name: 'Actual' };

// Columns with no clear control. Clearing the attribution window would store NULL, which means ACTUAL --
// the same as picking Actual outright - so a cleared cell just reappears as "Actual" after saving. The
// option list already covers every state, which leaves nothing for a clear button to express.
export const NON_CLEARABLE_FIELDS = [COHORT_PERIOD_FIELD];

// The campaign list is filtered server side, so a keystroke is a request.
export const SEARCH_DEBOUNCE_MS = 300;

// How many campaigns to ask for. The endpoint has no paging the grid uses, so this is the whole
// first page and anything beyond it is only reachable by typing. 500 is the endpoint's own cap.
export const CAMPAIGNS_LIMIT = 500;

// The dictionary-backed columns, in the display order used by the legacy Angular page.
// `field` is the row/payload key, `dict` is the dictionary that validates the cell.
export const COLUMNS = [
    {
        field: 'app',
        label: `${TRANSLATION_PREFIX}.COLUMNS.APP`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.APP`,
        dict: 'app',
    },
    {
        field: 'source',
        label: `${TRANSLATION_PREFIX}.COLUMNS.SOURCE`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.SOURCE`,
        dict: 'source',
    },
    {
        field: 'sub_adnetwork',
        label: `${TRANSLATION_PREFIX}.COLUMNS.SUB_ADNETWORK`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.SUB_ADNETWORK`,
        dict: 'sub_adnetwork',
    },
    {
        field: 'campaign_name',
        label: `${TRANSLATION_PREFIX}.COLUMNS.CAMPAIGN_NAME`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.CAMPAIGN_NAME`,
        dict: 'campaign_name',
        // Both per-row dictionaries depend on cells to their left, which is not visible from the
        // cell itself - the header says so rather than leaving an empty dropdown unexplained.
        tooltip: `${TRANSLATION_PREFIX}.COLUMN_TOOLTIP.CAMPAIGN_NAME`,
        // A campaign only means something under a source, so the cell is closed until the row has one and
        // says why. `disable` is what makes it a precondition rather than a suggestion.
        hintWhenEmpty: {
            fields: ['source'],
            placeholder: `${TRANSLATION_PREFIX}.COLUMNS.CAMPAIGN_NAME_HINT`,
            disable: true,
        },
    },
    {
        field: 'event',
        label: `${TRANSLATION_PREFIX}.COLUMNS.EVENT`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.EVENT`,
        dict: 'event',
        tooltip: `${TRANSLATION_PREFIX}.COLUMN_TOOLTIP.EVENT`,
        // Export the uuid, not the label: two of an app's slots can share a display name, so the label
        // would not re-import unambiguously. Mirrors REF_TABLE_CSV_ID_COLUMNS server-side; the importer
        // still accepts either the uuid or the readable label.
        csvValue: 'name',
    },
    {
        // Sits beside the event because it only means anything alongside one: it picks which of that
        // event's count columns the row is priced against. Its options are the org's cohort periods.
        field: 'cohort_period',
        label: `${TRANSLATION_PREFIX}.COLUMNS.COHORT_PERIOD`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.COHORT_PERIOD`,
        dict: 'cohort_period',
        tooltip: `${TRANSLATION_PREFIX}.COLUMN_TOOLTIP.COHORT_PERIOD`,
    },
    {
        field: 'os',
        label: `${TRANSLATION_PREFIX}.COLUMNS.OS`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.OS`,
        dict: 'os',
    },
    {
        field: 'device',
        label: `${TRANSLATION_PREFIX}.COLUMNS.DEVICE`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.DEVICE`,
        dict: 'device',
    },
    {
        field: 'country',
        label: `${TRANSLATION_PREFIX}.COLUMNS.COUNTRY`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.COUNTRY`,
        dict: 'country',
    },
    {
        // Options are the reading org's visible agencies, so they arrive with the table rather than
        // from the page-wide dictionary fetch.
        field: 'agency_id',
        label: `${TRANSLATION_PREFIX}.COLUMNS.AGENCY`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.AGENCY`,
        dict: 'agency_id',
        tooltip: `${TRANSLATION_PREFIX}.COLUMN_TOOLTIP.AGENCY`,
        // Export the agency_id, not the name: two agencies can share a display name and an ambiguous one
        // resolves to nothing on import, so a name-based export could not be re-imported. Mirrors
        // APIUtils.REF_TABLE_CSV_ID_COLUMNS on the server.
        csvValue: 'name',
    },
    {
        field: 'adn_account_id',
        label: `${TRANSLATION_PREFIX}.COLUMNS.ACCOUNT_ID`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.ACCOUNT_ID`,
        dict: 'adn_account_id',
    },
    {
        field: 'adn_original_currency',
        label: `${TRANSLATION_PREFIX}.COLUMNS.COST_CURRENCY`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.CURRENCY`,
        dict: 'adn_original_currency',
    },
    {
        field: 'tracker_original_currency',
        label: `${TRANSLATION_PREFIX}.COLUMNS.REVENUE_CURRENCY`,
        placeholder: `${TRANSLATION_PREFIX}.COLUMNS.CURRENCY`,
        dict: 'tracker_original_currency',
    },
];

// Every dictionary-backed field, including the optional value-currency column.
export const DICT_FIELDS = [...COLUMNS.map(column => column.field), VALUE_CURRENCY_FIELD];

// Columns whose dictionary is fetched per row, not per table (a table can hold several apps).
// `parents` is both the cache key and the set of cells that clear this one when they change.
// `requiresScope` means the endpoint can't be called without them: events need an app_id.
export const PER_ROW_DICTS = {
    event: { parents: ['app'], requiresScope: true },
    campaign_name: { parents: ['app', 'source'], requiresScope: false },
};

// The CSV column key for the date column (kept identical to the legacy export/import contract).
export const CSV_DATE_HEADER = 'Rule Start Date';
export const CSV_VALUE_HEADER = 'value';

// Example cell values shown in the import modal's "Expected format" table, keyed by CSV column.
// Illustrative only — the actual accepted values come from the on-page dropdowns.
export const CSV_COLUMN_EXAMPLES = {
    [CSV_DATE_HEADER]: '2026-07-14',
    app: 'com.company.app',
    source: 'facebook',
    sub_adnetwork: 'facebook',
    campaign_name: 'Summer_US_iOS',
    // The agency's name as the dropdown shows it, not its agency_id.
    agency_id: 'Acme Media',
    // The dropdown label, not the event uuid.
    event: 'Purchase',
    cohort_period: '7d',
    os: 'iOS',
    device: 'phone',
    country: 'US',
    adn_account_id: '1234567890',
    adn_original_currency: 'USD',
    tracker_original_currency: 'USD',
    [CSV_VALUE_HEADER]: '1250.00',
    [VALUE_CURRENCY_FIELD]: 'USD',
};

// Per-table help content, keyed by the table title returned from the backend.
export const TABLE_FAQ_LINKS = {
    'CPI Configuration':
        'https://support.singular.net/hc/en-us/articles/53658179746587-Reference-Tables-FAQ-Partner-Fees-and-CPI-Configuration',
    'Partner Fees':
        'https://support.singular.net/hc/en-us/articles/53658179746587-Reference-Tables-FAQ-Partner-Fees-and-CPI-Configuration',
};

// Value-column tooltip, keyed by table slug (matches the legacy ng-template tooltip).
export const VALUE_TOOLTIP_BY_SLUG = {
    'partner-fees': `${TRANSLATION_PREFIX}.VALUE_TOOLTIP.PARTNER_FEES`,
    'cpi-configuration': `${TRANSLATION_PREFIX}.VALUE_TOOLTIP.CPI_CONFIGURATION`,
};
